/**
 * Channel Listing Engine — Phase 3B: Authoritative Domain Library
 *
 * All dirty state, readiness, versions, and propagation are backed by
 * the database. The frontend reads canonical state, never fabricates it.
 */

import { supabase } from "@/lib/supabase";
import { mapCreateListingError, toCanonicalMediaStrategy } from "@/lib/media-strategy";
import { listingApprovalState } from "./listing-approval-semantics";
import { assertAmazonProviderWriteAllowed } from "@/lib/providers/amazon-write-guard";

// ---------------------------------------------------------------------------
// Core Types
// ---------------------------------------------------------------------------

export type ListingStatus =
  | "draft"
  | "ready"
  | "publishing"
  | "published"
  | "paused"
  | "error"
  | "drift"
  | "ended"
  | "archived";

export type PublicationStatus =
  | "not_published"
  | "publishing"
  | "published"
  | "drift"
  | "sync_failed"
  | "remote_review"
  | "cancelled"
  | "error"
  | "unpublishing"
  | "unpublished";

export type ReadinessStatus = "BLOCKED" | "READY_WITH_WARNINGS" | "READY";

export {
  readinessLabel,
  readinessTone,
  readinessAction,
  readinessCheckHref,
  readinessConnectorLine,
  type ReadinessTone,
} from "./listing-readiness";

export type ConnectionReadiness = "BLOCKED" | "READY" | "unknown";

export type DirtyDomain =
  | "content"
  | "variants"
  | "pricing"
  | "inventory"
  | "media"
  | "attributes"
  | "category"
  | "seo"
  | "shipping";

export type PriceStrategy =
  | "inherit"
  | "fixed_override"
  | "percentage_markup"
  | "fixed_markup";

export type InventoryStrategy =
  | "all_available"
  | "fixed_cap"
  | "percentage_allocation"
  | "location_specific";

export type MediaStrategy = "inherit" | "listing_override";

export interface ChannelListing {
  id: string;
  org_id: string;
  connector_id: string;
  product_id: string;
  external_listing_id: string | null;
  external_parent_id: string | null;
  status: ListingStatus;
  listing_type: string;
  channel_title: string | null;
  channel_description: string | null;
  channel_short_description: string | null;
  channel_subtitle: string | null;
  channel_condition_description: string | null;
  channel_category_id: string | null;
  channel_category_name: string | null;
  currency: string | null;
  price_strategy: PriceStrategy;
  inventory_strategy: InventoryStrategy;
  media_strategy: MediaStrategy;
  primary_media_filename?: string | null;
  publication_status: PublicationStatus;
  last_published_at: string | null;
  last_synced_at: string | null;
  last_error: string | null;
  provider_config: Record<string, unknown>;
  dirty_domains: DirtyDomain[];
  intended_version: number;
  operator_revision?: number | null;
  published_version: number;
  approved_version: number | null;
  approved_by: string | null;
  approved_at: string | null;
  connection_readiness: ConnectionReadiness;
  listing_readiness: ReadinessStatus | null;
  total_variant_count: number;
  included_variant_count: number;
  created_at: string;
  updated_at: string;
  // Joins
  connector?: { id: string; name: string; provider: string | null; status: string; auth_status: string; capabilities?: Record<string, unknown> };
  provider_confirmed_id?: string | null;
  product?: { id: string; name: string; sku: string | null; selling_price?: number | null };
  variant_count?: number;
  readiness_status?: string;
}

export interface ChannelListingVariant {
  id: string;
  org_id: string;
  channel_listing_id: string;
  variant_id: string;
  included: boolean;
  channel_sku: string | null;
  channel_price: number | null;
  effective_channel_price: number | null;
  last_published_channel_price: number | null;
  channel_barcode: string | null;
  external_variant_id: string | null;
  provider_config: Record<string, unknown>;
  variant?: {
    id: string;
    sku: string | null;
    barcode: string | null;
    option_values: Record<string, string> | null;
    selling_price: number | null;
    weight_kg?: number | null;
    length_cm?: number | null;
    width_cm?: number | null;
    height_cm?: number | null;
    status?: string;
  };
}

export interface ListingMediaItem {
  id: string;
  media_asset_id: string;
  storage_key: string | null;
  original_filename: string | null;
  width: number;
  height: number;
  role: string;
  position: number;
  is_primary: boolean;
  alt_text: string | null;
  source: string;
}

export interface ReadinessCheck {
  type: "blocking" | "warning";
  code: string;
  message: string;
  entity_type?: string | null;
    entity_id?: string | null;
  field?: string | null;
  href?: string | null;
  product_id?: string | null;
  listing_id?: string | null;
}

export interface ReadinessConnectorIdentity {
  id?: string;
  display_name: string;
  auth_status?: string | null;
  authenticated: boolean;
  auth_label: string;
}

export interface ReadinessResult {
  status: ReadinessStatus;
  blocking: number;
  warning: number;
  checks: ReadinessCheck[];
  connection_readiness?: ConnectionReadiness;
  connector?: ReadinessConnectorIdentity;
}

export interface ListingException {
  id: string;
  exception_type: string;
  severity: "info" | "warning" | "blocking";
  message: string;
  resolved: boolean;
  occurrence_count: number;
  last_seen_at: string;
  created_at: string;
}

export interface PublicationProjection {
  listing_id: string;
  intended_version: number;
  published_version: number;
  connector: Record<string, unknown>;
  product: Record<string, unknown>;
  content: Record<string, unknown>;
  pricing: Record<string, unknown>;
  inventory: Record<string, unknown>;
  variants: unknown[];
  media: unknown[];
  option_mappings: unknown[];
  category_mapping: Record<string, unknown> | null;
  identifiers: unknown[];
  readiness: Record<string, unknown> | null;
  dirty_domains: DirtyDomain[];
  provider_config: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Inheritance Helpers
// ---------------------------------------------------------------------------

export function resolveChannelTitle(listing: ChannelListing, productName: string): string {
  return listing.channel_title || productName;
}

export function resolveChannelDescription(listing: ChannelListing, productDescription: string | null): string {
  return listing.channel_description || productDescription || "";
}

export function isInheritingContent(listing: ChannelListing, field: "title" | "description" | "short_description" | "subtitle" | "condition_description"): boolean {
  switch (field) {
    case "title": return !listing.channel_title;
    case "description": return !listing.channel_description;
    case "short_description": return !listing.channel_short_description;
    case "subtitle": return !listing.channel_subtitle;
    case "condition_description": return !listing.channel_condition_description;
  }
}

export function isInheritingPrice(listing: ChannelListing): boolean {
  return listing.price_strategy === "inherit";
}

export function isInheritingMedia(listing: ChannelListing): boolean {
  return listing.media_strategy === "inherit";
}

export function hasDirtyDomain(listing: ChannelListing, domain: DirtyDomain): boolean {
  return listing.dirty_domains?.includes(domain) ?? false;
}

// ---------------------------------------------------------------------------
// Inventory Allocation Helpers
// ---------------------------------------------------------------------------

export interface AllocationPlan {
  totalAvailable: number;
  allocations: { connectorId: string; connectorName: string; qty: number; strategy: InventoryStrategy }[];
  unallocated: number;
}

export function computeAllocationPlan(
  totalAvailable: number,
  listings: { connector_id: string; connector_name: string; inventory_strategy: InventoryStrategy; channel_cap?: number }[],
): AllocationPlan {
  let remaining = totalAvailable;
  const allocations: AllocationPlan["allocations"] = [];

  for (const item of listings) {
    let qty = 0;
    if (item.inventory_strategy === "all_available") {
      qty = remaining;
    } else if (item.inventory_strategy === "fixed_cap" && item.channel_cap != null) {
      qty = Math.min(item.channel_cap, remaining);
    }
    allocations.push({
      connectorId: item.connector_id,
      connectorName: item.connector_name,
      qty,
      strategy: item.inventory_strategy,
    });
    remaining -= qty;
  }

  return { totalAvailable, allocations, unallocated: Math.max(0, remaining) };
}

// ---------------------------------------------------------------------------
// Oversale Protection
// ---------------------------------------------------------------------------

export function checkOversaleRisk(
  totalAvailable: number,
  fixedAllocations: { connectorId: string; qty: number }[],
): { safe: boolean; excess: number; details: string[] } {
  const sum = fixedAllocations.reduce((s, a) => s + a.qty, 0);
  if (sum <= totalAvailable) return { safe: true, excess: 0, details: [] };
  const excess = sum - totalAvailable;
  return {
    safe: false,
    excess,
    details: fixedAllocations.map((a) => `${a.connectorId}: ${a.qty}`),
  };
}

// ---------------------------------------------------------------------------
// Provider Config Helpers
// ---------------------------------------------------------------------------

export function getProviderConfig<T>(listing: ChannelListing, key: string, defaultValue: T): T {
  return (listing.provider_config?.[key] as T) ?? defaultValue;
}

export function setProviderConfig(listing: ChannelListing, key: string, value: unknown): Record<string, unknown> {
  return { ...listing.provider_config, [key]: value };
}

// ---------------------------------------------------------------------------
// Listing Lifecycle Helpers
// ---------------------------------------------------------------------------

export function canPublish(status: ListingStatus): boolean {
  return status === "draft" || status === "ready" || status === "paused" || status === "error" || status === "drift";
}

export function canEdit(status: ListingStatus): boolean {
  return status !== "archived" && status !== "ended";
}

export function canPause(status: ListingStatus): boolean {
  return status === "published" || status === "publishing";
}

export function canEnd(status: ListingStatus): boolean {
  return status === "published" || status === "paused" || status === "error" || status === "drift";
}

export function isPublished(status: ListingStatus): boolean {
  return status === "published" || status === "drift" || status === "paused";
}

// ---------------------------------------------------------------------------
// Approval Gate Helpers
// ---------------------------------------------------------------------------

/**
 * Operator-domain approval state. Catalogue intended_version still advances on
 * automatic price/inventory work; approval tracks operator_revision.
 */
export type ApprovalState = "none" | "approved" | "stale";

export function approvalState(listing: {
  approved_version: number | null;
  intended_version: number;
  operator_revision?: number | null;
  dirty_domains?: string[] | null;
}): ApprovalState {
  return listingApprovalState(listing);
}

// ---------------------------------------------------------------------------
// API Wrappers — Backend Authoritative
// ---------------------------------------------------------------------------

export async function fetchChannelListingsForProduct(orgId: string, productId: string): Promise<ChannelListing[]> {
  const { data, error } = await supabase.rpc("get_channel_listings_for_product", {
    p_org_id: orgId,
    p_product_id: productId,
  });
  if (error) throw error;
  const rows = (data as unknown as Array<ChannelListing & {
    connector_name?: string;
    connector_provider?: string;
    provider_confirmed_id?: string | null;
  }>) ?? [];
  return rows.map((row) => ({
    ...row,
    connector: row.connector ?? {
      id: row.connector_id,
      name: row.connector_name ?? "",
      provider: row.connector_provider ?? null,
      status: "active",
      auth_status: (row.connector as { auth_status?: string } | undefined)?.auth_status ?? "",
    },
    external_listing_id: row.external_listing_id ?? row.provider_confirmed_id ?? null,
  }));
}

export async function evaluateReadiness(orgId: string, listingId: string): Promise<ReadinessResult> {
  const { data, error } = await supabase.rpc("evaluate_listing_readiness", {
    p_org_id: orgId,
    p_listing_id: listingId,
  });
  if (error) throw error;
  const d = data as unknown as ReadinessResult;
  return d ?? { status: "BLOCKED", blocking: 1, warning: 0, checks: [] };
}

export async function createChannelListing(params: {
  orgId: string;
  connectorId: string;
  productId: string;
  channelTitle?: string;
  channelDescription?: string;
  listingType?: string;
  priceStrategy?: PriceStrategy;
  inventoryStrategy?: InventoryStrategy;
  mediaStrategy?: MediaStrategy;
  variantIds?: string[];
  channelCategoryId?: string;
  channelCategoryName?: string;
  providerConfig?: Record<string, unknown>;
  provider?: string | null;
}): Promise<{ success: boolean; listing_id?: string; error?: string; variant_count?: number; code?: string }> {
  const amazonWrite = assertAmazonProviderWriteAllowed(params.provider);
  if (amazonWrite.allowed === false) {
    return { success: false, error: amazonWrite.error, code: amazonWrite.code };
  }
  const mediaStrategy = toCanonicalMediaStrategy(params.mediaStrategy ?? "inherit");
  if (!mediaStrategy) {
    return { success: false, error: mapCreateListingError("invalid media_strategy") };
  }
  const existingCategory = ((params.providerConfig ?? {}).category as Record<string, unknown> | undefined) ?? {};
  const providerConfig = {
    ...(params.providerConfig ?? {}),
    ...(params.channelCategoryName
      ? { category: { ...existingCategory, name: params.channelCategoryName } }
      : {}),
  };
  const { data, error } = await supabase.rpc("create_channel_listing", {
    p_org_id: params.orgId,
    p_connector_id: params.connectorId,
    p_product_id: params.productId,
    p_channel_title: params.channelTitle ?? null,
    p_channel_description: params.channelDescription ?? null,
    p_listing_type: params.listingType ?? "single",
    p_price_strategy: params.priceStrategy ?? "inherit",
    p_inventory_strategy: params.inventoryStrategy ?? "all_available",
    p_media_strategy: mediaStrategy,
    p_variant_ids: params.variantIds ?? null,
    p_channel_category_id: params.channelCategoryId ?? null,
    p_provider_config: providerConfig,
  });
  if (error) {
    return { success: false, error: mapCreateListingError(error.message) };
  }
  const payload = (data as unknown as { success: boolean; listing_id?: string; error?: string; variant_count?: number; code?: string }) ?? { success: false };
  if (payload.error) payload.error = mapCreateListingError(payload.error);
  return payload;
}

export async function copyChannelListing(orgId: string, sourceListingId: string, targetConnectorId: string): Promise<{ success: boolean; listing_id?: string; error?: string }> {
  const { data, error } = await supabase.rpc("copy_channel_listing", {
    p_org_id: orgId,
    p_source_listing_id: sourceListingId,
    p_target_connector_id: targetConnectorId,
  });
  if (error) throw error;
  return (data as unknown as { success: boolean; listing_id?: string; error?: string }) ?? { success: false };
}

export async function archiveChannelListing(orgId: string, listingId: string): Promise<{ success: boolean; previous_status?: string; new_status?: string; error?: string }> {
  const { data, error } = await supabase.rpc("archive_channel_listing", {
    p_org_id: orgId,
    p_listing_id: listingId,
  });
  if (error) throw error;
  return (data as unknown as { success: boolean; previous_status?: string; new_status?: string; error?: string }) ?? { success: false };
}

export async function requestPublication(orgId: string, listingId: string): Promise<{ success: boolean; job_id?: string; error?: string; idempotency?: string; connection_readiness?: string; worker_deferred?: boolean }> {
  const { data, error } = await supabase.rpc("request_listing_publication", {
    p_org_id: orgId,
    p_listing_id: listingId,
  });
  if (error) throw error;
  const requested = (data as unknown as { success: boolean; job_id?: string; error?: string; idempotency?: string; connection_readiness?: string }) ?? { success: false };
  if (!requested.success || !requested.job_id) return requested;

  // Publication jobs are queued transactionally by the RPC, then consumed by
  // marketplace-sync. Trigger only the job we just created so the merchant's
  // Publish action cannot leave the listing indefinitely in "publishing".
  const { data: processed, error: processError } = await supabase.functions.invoke("marketplace-sync", {
    body: { action: "process-jobs", jobId: requested.job_id },
  });
  if (processError) {
    // The RPC already committed durable queued work. A failed browser wake-up
    // is not a failed publication request; the scheduled worker will claim it.
    return { ...requested, success: true, worker_deferred: true };
  }
  const result = (processed as { results?: Array<{ job_id?: string; ok?: boolean; error?: string }> } | null)?.results?.find((row) => row.job_id === requested.job_id);
  if (result && result.ok === false) {
    return { ...requested, success: false, error: result.error || "Publication failed" };
  }
  return requested;
}

export async function approveListingVersion(orgId: string, listingId: string): Promise<{ success: boolean; approved_version?: number; approved_by?: string; approved_at?: string; error?: string; code?: string }> {
  const { data, error } = await supabase.rpc("approve_listing_version", {
    p_org_id: orgId,
    p_listing_id: listingId,
  });
  if (error) throw error;
  return (data as unknown as { success: boolean; approved_version?: number; approved_by?: string; approved_at?: string; error?: string; code?: string }) ?? { success: false };
}

export async function transitionListingStatus(orgId: string, listingId: string, targetStatus: ListingStatus): Promise<{ success: boolean; previous_status?: string; error?: string }> {
  const { data, error } = await supabase.rpc("transition_listing_status", {
    p_org_id: orgId,
    p_listing_id: listingId,
    p_target_status: targetStatus,
  });
  if (error) throw error;
  return (data as unknown as { success: boolean; previous_status?: string; error?: string }) ?? { success: false };
}

export async function getPublicationProjection(orgId: string, listingId: string): Promise<PublicationProjection | null> {
  const { data, error } = await supabase.rpc("get_listing_publication_projection", {
    p_org_id: orgId,
    p_listing_id: listingId,
  });
  if (error) throw error;
  return (data as unknown as PublicationProjection) ?? null;
}

export async function safeArchiveProduct(orgId: string, productId: string): Promise<{ success: boolean; error?: string; blocking_listings?: string[]; suggested_action?: string }> {
  const { data, error } = await supabase.rpc("safe_archive_product", {
    p_org_id: orgId,
    p_product_id: productId,
  });
  if (error) throw error;
  return (data as unknown as { success: boolean; error?: string; blocking_listings?: string[]; suggested_action?: string }) ?? { success: false };
}

export async function validateInventoryAllocation(orgId: string, listingId: string, variantId: string, requestedQty: number): Promise<{ valid: boolean; reason?: string; available?: number; cap?: number; requested?: number }> {
  const { data, error } = await supabase.rpc("validate_inventory_allocation", {
    p_org_id: orgId,
    p_listing_id: listingId,
    p_variant_id: variantId,
    p_requested_qty: requestedQty,
  });
  if (error) throw error;
  return (data as unknown as { valid: boolean; reason?: string; available?: number; cap?: number; requested?: number }) ?? { valid: false, reason: "RPC returned null" };
}

export async function createSyncJob(orgId: string, listingId: string, jobType: string, payload?: Record<string, unknown>): Promise<{ success: boolean; job_id?: string; error?: string }> {
  const { data, error } = await supabase.rpc("create_sync_job_for_listing", {
    p_org_id: orgId,
    p_listing_id: listingId,
    p_job_type: jobType,
    p_payload: payload ?? {},
  });
  if (error) throw error;
  return (data as unknown as { success: boolean; job_id?: string; error?: string }) ?? { success: false };
}
